import { useLocation } from 'react-router';
import { EQueryStringParam, maxNameLength } from '../util/constants';
import API from '../api/api';
import GlobalContext from './GlobalContext';
import { validatePassword as checkPasswordIsValid } from '../util/validation/passwordValidation';
import { createContext, FC, useContext, useEffect, useState } from 'react';

export enum EGraduateUserUiState {
    default,
    queryStringParsed,
    ready,
    loading,
    success,
    fatalError,
    softError,
}

export type TBusiness = {
    id: number;
    name: string;
    locality: string;
    region: string;
    countryCode: string;
};

export type TGraduateUserContext = {
    graduateUserUiState: EGraduateUserUiState;
    errorMessage: string;
    userId: string;
    firstName: string;
    setFirstName: (name: string) => void;
    firstNameIsValid: boolean;
    firstNameValidationMessage: string;
    lastName: string;
    setLastName: (name: string) => void;
    lastNameIsValid: boolean;
    lastNameValidationMessage: string;
    email: string;
    password: string;
    setPassword: (password: string) => void;
    showPasswordValidation: boolean;
    setShowPasswordValidation: (show: boolean) => void;
    passwordValidationIsActive: boolean;
    countryCode: string;
    setCountryCode: (code: string) => void;
    submitGraduateUser: () => void;
    businesses: TBusiness[];
    disableSubmit: boolean;
    legacySyncToken: string;
};

const GraduateUserContext = createContext<TGraduateUserContext | undefined>(undefined);

export const GraduateUserProvider: FC = (props) => {
    const { language, getCountriesList, countriesList } = useContext(GlobalContext);
    const { children } = props;
    const { search: queryParams } = useLocation();
    const [errorMessage, setErrorMessage] = useState(language.validation.genericError);
    const [graduateUserUiState, setGraduateUserUiState] = useState(EGraduateUserUiState.default);
    const [userId, setUserId] = useState('');
    const [code, setCode] = useState('');
    const [firstName, setFirstName] = useState('');
    const [firstNameIsValid, setFirstNameIsValid] = useState(true);
    const [firstNameValidationMessage, setFirstNameValidationMessage] = useState('');
    const [lastName, setLastName] = useState('');
    const [lastNameIsValid, setLastNameIsValid] = useState(true);
    const [lastNameValidationMessage, setLastNameValidationMessage] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordIsValid, setPasswordIsValid] = useState(false);
    const [showPasswordValidation, setShowPasswordValidation] = useState(false);
    const [passwordValidationIsActive, setPasswordValidationIsActive] = useState(false);
    const [countryCode, setCountryCode] = useState('');
    const [disableSubmit, setDisableSubmit] = useState(true);
    const [businesses, setBusinesses] = useState<TBusiness[]>([]);
    const [legacySyncToken, setLegacySyncToken] = useState('');

    const parseQueryString = () => {
        const params = new URLSearchParams(queryParams);
        setUserId(params.get(EQueryStringParam.userId));
        setCode(params.get(EQueryStringParam.code));
        setGraduateUserUiState(EGraduateUserUiState.queryStringParsed);
    };

    const getInitialData = async () => {
        !countriesList.length && (await getCountriesList());
        try {
            const response = await API.get.startGraduation(queryParams);
            const { firstName, lastName, unconfirmedEmail, countryCode } = response.data;
            setFirstName(firstName);
            setLastName(lastName);
            setEmail(unconfirmedEmail);
            setCountryCode(countryCode);
            setGraduateUserUiState(EGraduateUserUiState.ready);
        } catch {
            setGraduateUserUiState(EGraduateUserUiState.fatalError);
            console.error('Failed to start graduating user');
        }
    };

    const validateFirstName = () => {
        let isValid = true;
        let validationMessage = '';
        if (firstName.length < 1) {
            isValid = false;
            validationMessage = language.validation.firstName;
        }
        if (firstName.length > maxNameLength) {
            isValid = false;
            validationMessage = language.validation.firstNameMaxLength;
        }
        setFirstNameIsValid(isValid);
        !isValid && setFirstNameValidationMessage(validationMessage);
    };

    const validateLastName = () => {
        let isValid = true;
        let validationMessage = '';
        if (lastName.length < 1) {
            isValid = false;
            validationMessage = language.validation.lastName;
        }
        if (lastName.length > maxNameLength) {
            isValid = false;
            validationMessage = language.validation.lastNameMaxLength;
        }
        setLastNameIsValid(isValid);
        !isValid && setLastNameValidationMessage(validationMessage);
    };

    const validatePassword = () => {
        const isValid = checkPasswordIsValid(password);
        setPasswordIsValid(isValid);
    };

    const checkDisableSubmit = () => {
        if (firstNameIsValid && lastNameIsValid && passwordIsValid) {
            setDisableSubmit(false);
        }
    };

    const submitGraduateUser = async () => {
        const postObject = {
            code,
            firstName,
            lastName,
            password,
            countryCode,
        };
        try {
            setGraduateUserUiState(EGraduateUserUiState.loading);
            const response = await API.post.completeGraduation(userId, postObject);
            const { businesses, legacyManualSyncToken = '' } = response.data;
            setBusinesses(businesses);
            setLegacySyncToken(legacyManualSyncToken);
            setGraduateUserUiState(EGraduateUserUiState.success);
        } catch {
            setGraduateUserUiState(EGraduateUserUiState.softError);
            console.error('Failed to graduate user');
        }
    };

    useEffect(() => {
        switch (graduateUserUiState) {
            case EGraduateUserUiState.default:
                parseQueryString();
                break;
            case EGraduateUserUiState.queryStringParsed:
                getInitialData();
                break;
            default:
                break;
        }
    }, [graduateUserUiState]);

    useEffect(() => {
        if (!countryCode && countriesList.length) {
            setCountryCode(countriesList[0].code);
        }
    }, [countriesList]);

    useEffect(() => {
        validateFirstName();
    }, [firstName]);

    useEffect(() => {
        validateLastName();
    }, [lastName]);

    useEffect(() => {
        if (!passwordValidationIsActive && password.length > 0) {
            setPasswordValidationIsActive(true);
        }
        validatePassword();
    }, [password]);

    useEffect(() => {
        checkDisableSubmit();
    });

    return (
        <GraduateUserContext.Provider
            value={{
                graduateUserUiState,
                userId,
                firstName,
                setFirstName,
                firstNameIsValid,
                firstNameValidationMessage,
                lastName,
                lastNameIsValid,
                lastNameValidationMessage,
                setLastName,
                email,
                password,
                setPassword,
                showPasswordValidation,
                setShowPasswordValidation,
                passwordValidationIsActive,
                countryCode,
                setCountryCode,
                businesses,
                submitGraduateUser,
                disableSubmit,
                errorMessage,
                legacySyncToken,
            }}
        >
            {children}
        </GraduateUserContext.Provider>
    );
};

export default GraduateUserContext;
